import React, { useState } from 'react';
import { X, Pencil, Trash2, Plus } from 'lucide-react';
import {
  useExpenseCategories,
  useCreateExpenseCategory,
  useUpdateExpenseCategory,
  useDeleteExpenseCategory,
  ExpenseCategory,
} from '../hooks/useExpenses.ts';
import { PermissionGate } from './PermissionGate';
import { PERMISSIONS } from '../utils/permissions';

type Props = {
  onClose: () => void;
};

const ExpenseCategoryManager = ({ onClose }: Props) => {
  const { data: categories = [], isLoading, error, refetch } = useExpenseCategories();
  const createCategory = useCreateExpenseCategory();
  const updateCategory = useUpdateExpenseCategory();
  const deleteCategory = useDeleteExpenseCategory();

  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');

  const startEdit = (cat: ExpenseCategory) => {
    setEditingId(cat.id);
    setEditName(cat.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const saveEdit = () => {
    if (!editingId || !editName.trim()) return;
    updateCategory.mutate(
      { id: editingId, name: editName.trim() },
      { onSuccess: cancelEdit }
    );
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    createCategory.mutate(newName.trim(), {
      onSuccess: () => setNewName(''),
    });
  };

  const handleDelete = (cat: ExpenseCategory) => {
    if (
      !window.confirm(
        `Delete category "${cat.name}"? This is only allowed if no expenses use it.`
      )
    ) {
      return;
    }
    deleteCategory.mutate(cat.id);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Expense categories</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md text-gray-500 hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
          <PermissionGate permission={PERMISSIONS.CREATE_EXPENSE}>
            <form onSubmit={handleCreate} className="flex gap-2">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="New category name"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                type="submit"
                disabled={!newName.trim() || createCategory.isPending}
                className="inline-flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                Add
              </button>
            </form>
          </PermissionGate>

          {isLoading && (
            <p className="text-sm text-gray-500 text-center py-4">Loading categories…</p>
          )}
          {error && (
            <p className="text-sm text-red-600 text-center py-2">
              Failed to load categories.{' '}
              <button
                type="button"
                onClick={() => refetch()}
                className="text-blue-600 underline"
              >
                Retry
              </button>
            </p>
          )}
          {!isLoading && !error && categories.length === 0 && (
            <p className="text-sm text-gray-600 text-center py-4">
              No categories yet. Add one above.
            </p>
          )}

          {!isLoading && !error && categories.length > 0 && (
            <ul className="divide-y border rounded-md">
              {categories.map((cat) => (
                <li
                  key={cat.id}
                  className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-gray-50"
                >
                  {editingId === cat.id ? (
                    <div className="flex flex-1 gap-2 items-center">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={saveEdit}
                        disabled={!editName.trim() || updateCategory.isPending}
                        className="text-sm text-blue-600 font-medium disabled:opacity-50"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="text-sm text-gray-600"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className="text-sm text-gray-900 flex-1">{cat.name}</span>
                      <div className="flex items-center gap-1 shrink-0">
                        <PermissionGate permission={PERMISSIONS.EDIT_EXPENSE}>
                          <button
                            type="button"
                            onClick={() => startEdit(cat)}
                            className="p-1.5 text-gray-600 hover:bg-gray-200 rounded"
                            aria-label={`Edit ${cat.name}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(cat)}
                            disabled={deleteCategory.isPending}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                            aria-label={`Delete ${cat.name}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </PermissionGate>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t px-6 py-3 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExpenseCategoryManager;
